import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowDown, ArrowUp, Eye, Pencil, Plus, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail, mediaUrl } from "../lib/api";
import RichEditor from "./RichEditor";
import MediaPicker from "./MediaPicker";

const BOOK_TYPES = ["Buku Fisik", "E-Book", "Panduan", "Karya Lain"];
const EMPTY = {
  title: "", slug: "", subtitle: "", type: "Buku Fisik", description: "", benefits: [],
  audience: "", contents: "", sample_note: "", cover_id: null, gallery: [], price: "",
  price_note: "", checkout_url: "", faq: [], featured: false, status: "draft",
  cta_label: "", guarantee_note: "", testimonials: [],
};

export default function AdminBooks({ editorOpen = false }) {
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(editorOpen || !!id ? id || "new" : null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [coverPicker, setCoverPicker] = useState(false);
  const [galleryPicker, setGalleryPicker] = useState(false);

  const load = () => {
    setLoading(true);
    setError("");
    api.get("/admin/books")
      .then((r) => setItems(r.data.items))
      .catch((err) => {
        setItems([]);
        if (err.response?.status !== 401) setError(formatApiErrorDetail(err.response?.data?.detail));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (editing && editing !== "new") {
      api.get(`/admin/books/${editing}`).then((r) => setForm({ ...EMPTY, ...r.data })).catch(() => setError("Karya tidak ditemukan"));
    } else if (editing === "new") {
      setForm({ ...EMPTY });
    }
  }, [editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (statusOverride) => {
    setError("");
    try {
      const payload = { ...form, status: statusOverride || form.status };
      if (editing === "new") {
        const { data } = await api.post("/admin/books", payload);
        setEditing(data.id);
      } else {
        await api.put(`/admin/books/${editing}`, payload);
      }
      setSaved("Tersimpan");
      setTimeout(() => setSaved(""), 2000);
      if (statusOverride) setForm((f) => ({ ...f, status: statusOverride }));
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const del = async (bid) => {
    if (!window.confirm("Hapus karya ini?")) return;
    setError("");
    try {
      await api.delete(`/admin/books/${bid}`);
      if (editing === bid) setEditing(null);
      load();
    } catch (err) {
      if (err.response?.status !== 401) setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reorder = async (i, dir) => {
    const arr = [...items];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setItems(arr);
    try {
      await api.post("/admin/books/reorder", { ids: arr.map((a) => a.id) });
    } catch (err) {
      setItems(items);
      if (err.response?.status !== 401) setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (editing && form) {
    return (
      <div data-testid="book-editor">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <button onClick={() => { setEditing(null); setForm(null); }} className="text-[13px] text-sub hover:text-ink">&larr; Semua karya</button>
            <h1 className="font-serif text-3xl text-ink mt-2">{editing === "new" ? "Tambah Karya" : "Sunting Karya"}</h1>
          </div>
          <span data-testid="book-save-indicator" className="text-[13px] font-semibold text-moss">{saved}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          <div className="lg:col-span-2 space-y-5">
            <div>
              <label className="field-label" htmlFor="bk-title">Judul *</label>
              <input id="bk-title" data-testid="book-title-input" required value={form.title} onChange={(e) => set("title", e.target.value)} className="field-input !text-lg" placeholder="Judul buku/karya" />
            </div>
            <div>
              <label className="field-label" htmlFor="bk-subtitle">Subjudul / manfaat singkat</label>
              <input id="bk-subtitle" data-testid="book-subtitle-input" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} className="field-input" />
            </div>
            <div>
              <label className="field-label">Deskripsi</label>
              <RichEditor value={form.description} onChange={(html) => set("description", html)} />
            </div>
            <div>
              <label className="field-label" htmlFor="bk-benefits">Manfaat utama (satu per baris)</label>
              <textarea id="bk-benefits" data-testid="book-benefits-input" value={(form.benefits || []).join("\n")} onChange={(e) => set("benefits", e.target.value.split("\n").filter(Boolean))} className="field-input !min-h-[100px]" />
            </div>
            <div>
              <label className="field-label" htmlFor="bk-audience">Untuk siapa karya ini dibuat</label>
              <textarea id="bk-audience" data-testid="book-audience-input" value={form.audience} onChange={(e) => set("audience", e.target.value)} className="field-input !min-h-[80px]" />
            </div>
            <div>
              <label className="field-label">Isi / daftar bab</label>
              <RichEditor value={form.contents} onChange={(html) => set("contents", html)} />
            </div>
            <div className="bg-surface border border-line rounded-lg p-5">
              <p className="font-semibold text-ink text-sm mb-3">FAQ</p>
              {(form.faq || []).map((f, i) => (
                <div key={i} className="flex flex-col sm:flex-row gap-2 mb-2">
                  <input value={f.q || ""} data-testid={`book-faq-q-${i}`} onChange={(e) => { const arr = [...form.faq]; arr[i] = { ...arr[i], q: e.target.value }; set("faq", arr); }} placeholder="Pertanyaan" className="field-input !min-h-[40px]" />
                  <input value={f.a || ""} data-testid={`book-faq-a-${i}`} onChange={(e) => { const arr = [...form.faq]; arr[i] = { ...arr[i], a: e.target.value }; set("faq", arr); }} placeholder="Jawaban" className="field-input !min-h-[40px]" />
                  <button type="button" aria-label="Hapus FAQ" onClick={() => set("faq", form.faq.filter((_, j) => j !== i))} className="w-10 h-10 rounded-md border border-line text-sub hover:text-enji shrink-0"><Trash2 size={15} className="mx-auto" /></button>
                </div>
              ))}
              <button type="button" data-testid="book-add-faq" onClick={() => set("faq", [...(form.faq || []), { q: "", a: "" }])} className="btn-ghost !min-h-[40px] !px-4 text-sm"><Plus size={14} /> Tambah FAQ</button>
            </div>
            <div className="bg-surface border border-line rounded-lg p-5">
              <p className="font-semibold text-ink text-sm mb-3">Testimoni Pembaca — hanya diisi jika asli (kosong = bagian disembunyikan)</p>
              {(form.testimonials || []).map((t, i) => (
                <div key={i} className="flex flex-col sm:flex-row gap-2 mb-2">
                  <input value={t.text || ""} data-testid={`book-testimonial-text-${i}`} onChange={(e) => { const arr = [...form.testimonials]; arr[i] = { ...arr[i], text: e.target.value }; set("testimonials", arr); }} placeholder="Kata pembaca…" className="field-input !min-h-[40px] flex-1" />
                  <input value={t.name || ""} data-testid={`book-testimonial-name-${i}`} onChange={(e) => { const arr = [...form.testimonials]; arr[i] = { ...arr[i], name: e.target.value }; set("testimonials", arr); }} placeholder="Nama" className="field-input !min-h-[40px] !w-full sm:!w-36" />
                  <input value={t.role || ""} data-testid={`book-testimonial-role-${i}`} onChange={(e) => { const arr = [...form.testimonials]; arr[i] = { ...arr[i], role: e.target.value }; set("testimonials", arr); }} placeholder="Peran (ops.)" className="field-input !min-h-[40px] !w-full sm:!w-32" />
                  <button type="button" aria-label="Hapus testimoni" onClick={() => set("testimonials", form.testimonials.filter((_, j) => j !== i))} className="w-10 h-10 rounded-md border border-line text-sub hover:text-enji shrink-0"><Trash2 size={15} className="mx-auto" /></button>
                </div>
              ))}
              <button type="button" data-testid="book-add-testimonial" onClick={() => set("testimonials", [...(form.testimonials || []), { text: "", name: "", role: "" }])} className="btn-ghost !min-h-[40px] !px-4 text-sm"><Plus size={14} /> Tambah testimoni</button>
            </div>
            <div className="bg-surface border border-line rounded-lg p-5 space-y-3">
              <p className="font-semibold text-ink text-sm">Ajakan Beli &amp; Kepercayaan</p>
              <div>
                <label className="field-label" htmlFor="bk-cta">Label tombol beli</label>
                <input id="bk-cta" data-testid="book-cta-label-input" value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} maxLength={120} className="field-input !min-h-[40px]" placeholder="Beli di Lynk.id" />
                <p className="mt-1 text-xs text-muted text-right">{form.cta_label.length}/120</p>
              </div>
              <div>
                <label className="field-label" htmlFor="bk-guarantee">Catatan jaminan / kepercayaan</label>
                <textarea id="bk-guarantee" data-testid="book-guarantee-input" value={form.guarantee_note} onChange={(e) => set("guarantee_note", e.target.value)} maxLength={500} className="field-input !min-h-[80px]" placeholder="Mis. pengiriman 2 hari kerja, dukungan penuh, dsb. (isi jujur sesuai kebijakan Anda)" />
                <p className="mt-1 text-xs text-muted text-right">{form.guarantee_note.length}/500</p>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <div className="bg-surface border border-line rounded-lg p-5 space-y-3">
              <p className="font-semibold text-ink text-sm">Publikasi</p>
              <div>
                <label className="field-label" htmlFor="bk-type">Format / jenis karya</label>
                <select id="bk-type" data-testid="book-type-select" value={form.type} onChange={(e) => set("type", e.target.value)} className="field-input !min-h-[40px]">
                  {BOOK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2.5 text-sm text-ink cursor-pointer">
                <input type="checkbox" data-testid="book-featured-checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[var(--enji)]" />
                Jadikan unggulan di beranda
              </label>
              <div className="flex flex-wrap gap-2 pt-2">
                <button type="button" data-testid="book-save-button" onClick={() => save()} className="btn-ghost !min-h-[40px] !px-4 text-sm">Simpan</button>
                <button type="button" data-testid="book-publish-button" onClick={() => save(form.status === "published" ? "draft" : "published")} className="btn-primary !min-h-[40px] !px-4 text-sm">
                  {form.status === "published" ? "Jadikan Draf" : "Terbitkan"}
                </button>
                {editing !== "new" && (
                  <button type="button" data-testid="book-delete-button" onClick={() => del(editing)} className="btn-ghost !min-h-[40px] !px-4 text-sm !text-enji">Hapus</button>
                )}
              </div>
              {error && <p role="alert" className="text-[13px] text-enji">{error}</p>}
            </div>

            <div className="bg-surface border border-line rounded-lg p-5">
              <p className="font-semibold text-ink text-sm mb-3">Cover</p>
              {form.cover_id ? (
                <div className="relative rounded-md overflow-hidden border border-line" style={{ aspectRatio: "3/4" }}>
                  <img src={mediaUrl(form.cover_id)} alt="Cover" className="w-full h-full object-cover" />
                  <button type="button" data-testid="book-cover-remove" onClick={() => set("cover_id", null)} aria-label="Hapus cover" className="absolute top-2 right-2 w-8 h-8 rounded-full bg-ivory/90 text-enji">×</button>
                </div>
              ) : (
                <button type="button" data-testid="book-cover-pick" onClick={() => setCoverPicker(true)} className="btn-ghost !min-h-[40px] w-full text-sm">Pilih / unggah cover</button>
              )}
            </div>

            <div className="bg-surface border border-line rounded-lg p-5">
              <p className="font-semibold text-ink text-sm mb-3">Galeri Visual (4–6 disarankan)</p>
              <div className="grid grid-cols-3 gap-2">
                {(form.gallery || []).map((gid, i) => (
                  <div key={gid} className="relative rounded-md overflow-hidden border border-line" style={{ aspectRatio: "1" }}>
                    <img src={mediaUrl(gid)} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-ivory/80 py-0.5">
                      <button type="button" aria-label="Geser kiri" onClick={() => { const arr = [...form.gallery]; [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]]; set("gallery", arr); }} disabled={i === 0} className="text-sub disabled:opacity-30"><ArrowUp size={13} /></button>
                      <button type="button" aria-label="Hapus" data-testid={`book-gallery-remove-${i}`} onClick={() => set("gallery", form.gallery.filter((_, j) => j !== i))} className="text-enji"><X size={13} /></button>
                      <button type="button" aria-label="Geser kanan" onClick={() => { const arr = [...form.gallery]; [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]]; set("gallery", arr); }} disabled={i === form.gallery.length - 1} className="text-sub disabled:opacity-30"><ArrowDown size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" data-testid="book-gallery-add" onClick={() => setGalleryPicker(true)} className="btn-ghost !min-h-[40px] w-full text-sm mt-3">Tambah visual</button>
              <p className="text-[12px] text-faint mt-3">Unggah: cover depan, mockup dari cover asli, cuplikan halaman, dan visual konteks. Mockup bersifat ilustratif — jangan diklaim sebagai dokumentasi nyata.</p>
            </div>

            <div className="bg-surface border border-line rounded-lg p-5 space-y-3">
              <p className="font-semibold text-ink text-sm">Harga &amp; Checkout Lynk.id</p>
              <div>
                <label className="field-label" htmlFor="bk-price">Harga (opsional)</label>
                <input id="bk-price" data-testid="book-price-input" value={form.price} onChange={(e) => set("price", e.target.value)} className="field-input !min-h-[40px]" placeholder="Rp49.000" />
              </div>
              <div>
                <label className="field-label" htmlFor="bk-price-note">Keterangan harga</label>
                <input id="bk-price-note" data-testid="book-price-note-input" value={form.price_note} onChange={(e) => set("price_note", e.target.value)} className="field-input !min-h-[40px]" placeholder="Harga mengikuti informasi di Lynk.id" />
              </div>
              <div>
                <label className="field-label" htmlFor="bk-checkout">checkout_url</label>
                <input id="bk-checkout" data-testid="book-checkout-input" value={form.checkout_url} onChange={(e) => set("checkout_url", e.target.value)} className="field-input !min-h-[40px]" placeholder="https://lynk.id/akunanda/produk" />
                <p className="text-[12px] text-faint mt-1">Harus HTTPS menuju domain lynk.id. Kosongkan untuk menonaktifkan tombol beli.</p>
              </div>
              <div>
                <label className="field-label" htmlFor="bk-sample">Catatan sampel</label>
                <textarea id="bk-sample" data-testid="book-sample-note-input" value={form.sample_note} onChange={(e) => set("sample_note", e.target.value)} className="field-input !min-h-[70px]" placeholder="Catatan tentang halaman contoh yang boleh dibaca publik" />
              </div>
            </div>
          </div>
        </div>

        <MediaPicker open={coverPicker} onClose={() => setCoverPicker(false)} onPick={(m) => { set("cover_id", m.id); setCoverPicker(false); }} />
        <MediaPicker open={galleryPicker} onClose={() => setGalleryPicker(false)} onPick={(m) => { set("gallery", [...(form.gallery || []), m.id].slice(0, 8)); setGalleryPicker(false); }} />
      </div>
    );
  }

  return (
    <div data-testid="admin-books-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="font-serif text-3xl text-ink">Buku &amp; Karya</h1>
        <button onClick={() => { setEditing("new"); }} data-testid="admin-new-book-button" className="btn-primary !min-h-[40px]"><Plus size={15} /> Tambah Karya</button>
      </div>

      {loading ? (
        <p className="text-sub text-sm mt-8">Memuat…</p>
      ) : items.length === 0 ? (
        <p className="text-sub text-sm mt-8">Belum ada karya. Tambahkan karya pertama Anda.</p>
      ) : (
        <div className="mt-6 bg-surface border border-line rounded-xl overflow-hidden">
          <ul className="divide-y divide-line">
            {items.map((b, i) => (
              <li key={b.id} className="p-4 sm:p-5 flex items-center gap-4" data-testid={`admin-book-row-${b.id}`}>
                <div className="w-12 h-16 rounded overflow-hidden bg-surface-muted border border-line shrink-0">
                  {b.cover_id && <img src={mediaUrl(b.cover_id)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink truncate">{b.title}</p>
                  <p className="text-[13px] text-faint mt-1">{b.type} · {b.status === "published" ? "Terbit" : "Draf"} · {b.checkout_url ? "checkout aktif" : "checkout kosong"}</p>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => reorder(i, -1)} aria-label="Naikkan urutan" className="w-9 h-9 rounded-md border border-line text-sub hover:text-ink"><ArrowUp size={14} className="mx-auto" /></button>
                  <button onClick={() => reorder(i, 1)} aria-label="Turunkan urutan" className="w-9 h-9 rounded-md border border-line text-sub hover:text-ink"><ArrowDown size={14} className="mx-auto" /></button>
                  {b.status === "published" && (
                    <Link to={`/buku-karya/${b.slug}`} target="_blank" aria-label="Lihat di situs" className="w-9 h-9 rounded-md border border-line flex items-center justify-center text-sub hover:text-enji"><Eye size={15} /></Link>
                  )}
                  <button onClick={() => setEditing(b.id)} data-testid={`admin-book-edit-${b.id}`} aria-label="Sunting" className="w-9 h-9 rounded-md border border-line flex items-center justify-center text-sub hover:text-enji"><Pencil size={15} /></button>
                  <button onClick={() => del(b.id)} data-testid={`admin-book-delete-${b.id}`} aria-label="Hapus" className="w-9 h-9 rounded-md border border-line flex items-center justify-center text-sub hover:text-enji"><Trash2 size={15} /></button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
